import { FC, useEffect, useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import { Carousel, Column, Columns, Hidden, PlanCard, Section } from '@belong/ui-components';
import { IPlanCard } from '@belong/types';
import { useCampaignOverride } from '@belong/offers';
import { useToggleIndexes } from '@belong/react-hooks/useToggleIndexes';
import { isBrowser } from '@belong/env';
import { SECTION_PLAN_SUMMARY_TEST_ID as TEST_ID } from './test-ids';
import { ICommonPlanCardProps, ISectionPlanSummary, isIPlanCard } from './model';
import { planStatusFilter } from './planStatusFilter';
import { SectionFooter } from './SectionFooter';
import { useTallestElementHeight } from './useTallestElementHeight';
import { useTallestListItemHeight } from './useTallestListItemHeight';

const INITIAL_PLAN_PARAM = 'show_plan';

export const PlanSummary: FC<ISectionPlanSummary> = ({
  id,
  identifier,
  plans,
  carousel,
  footerContent,
  sectionStyles,
  focusTextOnSelect = true,
  accordionRows,
  isPreSq
}: ISectionPlanSummary) => {
  const [initialPlan, setInitialPlan] = useState<number>(0);

  const activePlans = plans.filter(plan => planStatusFilter(plan));
  const activePlanCards = activePlans.filter(isIPlanCard) as IPlanCard[];
  const summaryContainsDifferentiator = activePlanCards.some(plan => plan.differentiatorLabel || plan.current);
  const displayAsCarousel = carousel && activePlanCards.length > 2;
  // this is used to determine if we need to offset price metrics that don't have a promo. Not needed if there are no promos in the plans
  const planGroupIncludesPromo = activePlanCards.some(plan => plan.priceBeforePromo);

  // load campaign terms and conditions (defaults to footerContent param if not available)
  const { override: footerOverride, isLoading: isLoadingCampaignOverride } = useCampaignOverride(
    'planSelectionTerms',
    activePlanCards[0]?.plan.productFamily
  );

  useEffect(() => {
    // get the params from the query string
    if (isBrowser()) {
      const qs = new URLSearchParams(window.location.search);
      const showPlanTitle = qs.get(INITIAL_PLAN_PARAM);

      // there may be a query string defining the plan to show initially
      const showPlanIndex = activePlanCards.findIndex(plan => kebabCase(plan.title).toLowerCase() === showPlanTitle);
      if (showPlanIndex !== -1) {
        setInitialPlan(showPlanIndex);
      }
    }
  }, [activePlanCards]);

  const { indexRefs: accordionRefs, toggleIndexes: toggleAccordions } = useToggleIndexes();
  const { indexRefs: carouselAccordionRefs, toggleIndexes: carouselToggleAccordions } = useToggleIndexes();

  const {
    elementRefs: calloutRefs,
    tallestElementHeight: tallestCalloutHeight,
    removeHeight: removeCalloutHeight
  } = useTallestElementHeight();

  const {
    elementRefs: carouselCalloutRefs,
    tallestElementHeight: tallestCarouselCalloutHeight,
    removeHeight: removeCarouselCalloutHeight
  } = useTallestElementHeight();

  const {
    elementRefs: alertRefs,
    tallestElementHeight: tallestAlertHeight,
    removeHeight: removeAlertHeight
  } = useTallestElementHeight();

  const {
    elementRefs: carouselAlertRefs,
    tallestElementHeight: tallestCarouselAlertHeight,
    removeHeight: removeCarouselAlertHeight
  } = useTallestElementHeight();

  const { elementRefs: accordionRowContentRefs } = useTallestListItemHeight();
  const { elementRefs: carouselAccordionRowContentRefs } = useTallestListItemHeight();

  const {
    elementRefs: activityLevelRefs,
    tallestElementHeight: tallestActivityLevelHeight,
    removeHeight: removeActivityLevelHeight
  } = useTallestElementHeight();

  const {
    elementRefs: carouselActivityLevelRefs,
    tallestElementHeight: tallestCarouselActivityLevelHeight,
    removeHeight: removeCarouselActivityLevelHeight
  } = useTallestElementHeight();

  const {
    elementRefs: disclaimerRefs,
    tallestElementHeight: tallestDisclaimerHeight,
    removeHeight: removeDisclaimerHeight
  } = useTallestElementHeight();

  const {
    elementRefs: carouselDisclaimerRefs,
    tallestElementHeight: tallestCarouselDisclaimerHeight,
    removeHeight: removeCarouselDisclaimerHeight
  } = useTallestElementHeight();

  const getCommonPlanCardProps = (isCarousel: boolean): ICommonPlanCardProps => {
    return {
      hasTopPadding: summaryContainsDifferentiator,
      accordionRefs: isCarousel ? carouselAccordionRefs : accordionRefs,
      toggleAccordions: isCarousel ? carouselToggleAccordions : toggleAccordions,
      calloutRefs: isCarousel ? carouselCalloutRefs : calloutRefs,
      calloutHeight: isCarousel ? tallestCarouselCalloutHeight : tallestCalloutHeight,
      removeCalloutHeight: isCarousel ? removeCarouselCalloutHeight : removeCalloutHeight,
      alertRefs: isCarousel ? carouselAlertRefs : alertRefs,
      alertHeight: isCarousel ? tallestCarouselAlertHeight : tallestAlertHeight,
      removeAlertHeight: isCarousel ? removeCarouselAlertHeight : removeAlertHeight,
      accordionRowContentRefs: isCarousel ? carouselAccordionRowContentRefs : accordionRowContentRefs,
      activityLevelRefs: isCarousel ? carouselActivityLevelRefs : activityLevelRefs,
      activityLevelHeight: isCarousel ? tallestCarouselActivityLevelHeight : tallestActivityLevelHeight,
      removeActivityLevelHeight: isCarousel ? removeCarouselActivityLevelHeight : removeActivityLevelHeight,
      disclaimerRefs: isCarousel ? carouselDisclaimerRefs : disclaimerRefs,
      disclaimerHeight: isCarousel ? tallestCarouselDisclaimerHeight : tallestDisclaimerHeight,
      removeDisclaimerHeight: isCarousel ? removeCarouselDisclaimerHeight : removeDisclaimerHeight,
      focusTextOnSelect,
      planGroupIncludesPromo,
      isPreSq
    };
  };

  if (!activePlanCards.length || isLoadingCampaignOverride) {
    return null;
  }

  const termsAndConditions = footerOverride?.html || footerContent;

  // CAROUSEL LAYOUT
  if (displayAsCarousel) {
    return (
      <Section
        data-contentid={id}
        data-identifier={identifier}
        data-testid={TEST_ID.section}
        role="region"
        aria-label="plan card carousel"
        {...sectionStyles}
      >
        <Hidden atAndAbove="xl">
          <Carousel data-testid={TEST_ID.carousel} initialSlide={initialPlan}>
            {activePlanCards.map((plan, index) => (
              <PlanCard
                {...plan}
                columnIndex={index}
                key={`${plan.plan.productCode}_${plan.title}`}
                {...getCommonPlanCardProps(true)}
                isWithinCarousel={displayAsCarousel}
              />
            ))}
          </Carousel>
        </Hidden>
        <Hidden below="xl">
          <Columns space={{ lg: 'xsmall', xl: 'medium' }} collapseBelow="lg">
            {activePlanCards.map((plan, index) => (
              <Column
                alignY="stretch"
                alignX="center"
                width={{ lg: '1/3' }}
                key={`column_${plan.plan.productCode}_${plan.title}`}
                role="radiogroup"
              >
                <PlanCard {...plan} columnIndex={index} {...getCommonPlanCardProps(false)} />
              </Column>
            ))}
          </Columns>
        </Hidden>
        <SectionFooter footerContent={termsAndConditions} accordionRows={accordionRows} />
      </Section>
    );
  }

  // LESS THAN THREE PLANS - NO CAROUSEL
  return (
    <Section data-contentid={id} data-identifier={identifier} data-testid={TEST_ID.section} {...sectionStyles}>
      <Columns space={{ xs: 'large', lg: 'xsmall', xl: 'medium' }} collapseBelow="lg" alignX="center">
        {activePlanCards.map((plan, index) => (
          <Column
            alignY="stretch"
            alignX="center"
            width={{ lg: '1/3' }}
            key={`column_${plan.plan.productCode}`}
            role="radiogroup"
          >
            <PlanCard {...plan} columnIndex={index} {...getCommonPlanCardProps(false)} />
          </Column>
        ))}
      </Columns>
      <SectionFooter footerContent={termsAndConditions} accordionRows={accordionRows} />
    </Section>
  );
};

PlanSummary.displayName = 'PlanSummary';
export default PlanSummary;
